'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

export interface AddressAutocompleteResult {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

// NOTE: Switch to Google Places API (New) once enabled at:
// https://console.developers.google.com/apis/api/places.googleapis.com/overview?project=398507082084
// Currently uses Nominatim (OpenStreetMap) — free, no key required, Canada-restricted

const PROVINCE_CODES: Record<string, string> = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Northwest Territories': 'NT',
  'Nova Scotia': 'NS',
  Nunavut: 'NU',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Quebec: 'QC',
  Saskatchewan: 'SK',
  Yukon: 'YT',
};

interface NominatimAddress {
  house_number?: string;
  road?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  state?: string;
  postcode?: string;
}

interface PlaceSuggestion {
  id: string;
  mainText: string;
  secondaryText: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  rawAddress: NominatimAddress;
}

async function searchNominatim(query: string): Promise<PlaceSuggestion[]> {
  if (query.trim().length < 3) return [];

  try {
    const params = new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: '1',
      limit: '6',
      countrycodes: 'ca',
      'accept-language': 'en',
    });

    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      headers: { 'User-Agent': 'Hummii/1.0 (hummii.ca)' },
    });

    if (!res.ok) return [];

    const data: Array<{ place_id: number; lat: string; lon: string; address: NominatimAddress }> =
      await res.json();

    return data.map((item) => {
      const addr = item.address;
      const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
      const city = addr.city || addr.town || addr.village || addr.municipality || '';
      const state = addr.state || '';
      const postcode = (addr.postcode || '').toUpperCase();

      const mainText = street || city;
      const secondaryText = [city, state, postcode].filter(Boolean).join(', ');
      const formattedAddress = [street, city, state, postcode, 'Canada']
        .filter(Boolean)
        .join(', ');

      return {
        id: item.place_id.toString(),
        mainText,
        secondaryText,
        formattedAddress,
        latitude: parseFloat(item.lat),
        longitude: parseFloat(item.lon),
        rawAddress: addr,
      };
    });
  } catch (err) {
    console.error('[AddressAutocomplete] Nominatim search failed:', err);
    return [];
  }
}

function buildResult(suggestion: PlaceSuggestion): AddressAutocompleteResult {
  const addr = suggestion.rawAddress;
  return {
    address: [addr.house_number, addr.road].filter(Boolean).join(' '),
    city: addr.city || addr.town || addr.village || addr.municipality || '',
    province: PROVINCE_CODES[addr.state || ''] || (addr.state || ''),
    postalCode: (addr.postcode || '').toUpperCase(),
    latitude: suggestion.latitude,
    longitude: suggestion.longitude,
    formattedAddress: suggestion.formattedAddress,
  };
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: AddressAutocompleteResult) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  error,
  disabled,
  className = '',
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipSearchRef = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerSearch = useCallback(
    (inputValue: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!inputValue.trim() || inputValue.length < 3) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        const results = await searchNominatim(inputValue);
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
        setIsLoading(false);
      }, 400);
    },
    []
  );

  const handleInputChange = useCallback(
    (inputValue: string) => {
      onChange(inputValue);

      if (skipSearchRef.current) {
        skipSearchRef.current = false;
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      triggerSearch(inputValue);
    },
    [onChange, triggerSearch]
  );

  const handleSelect = useCallback(
    (suggestion: PlaceSuggestion) => {
      skipSearchRef.current = true;
      onChange(suggestion.formattedAddress);
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      onSelect(buildResult(suggestion));
    },
    [onChange, onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [isOpen, suggestions, activeIndex, handleSelect]
  );

  const inputClass = [
    'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2',
    error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-transparent',
    disabled ? 'bg-gray-50 cursor-not-allowed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={inputClass}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li key={s.id} role="option" aria-selected={i === activeIndex}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(s);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full px-4 py-3 text-left transition-colors border-b border-gray-100 last:border-b-0 ${
                  i === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <p className="text-sm font-medium text-gray-900 truncate">{s.mainText}</p>
                {s.secondaryText && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{s.secondaryText}</p>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
